import { useState, useEffect, useCallback } from 'react'
import Project from 'common/project'
import _data from 'common/utils/_data'
import { data } from 'data/footerData'
import { City, useTopCities } from 'common/hooks/useTopCities'

const convertToUrl = (term) => term.toLowerCase().replace(/\s+/g, '-')

const splitData = (data: string[] | City[]) => {
  const maxEntries = 20
  const groupSize = 5
  const sliceData = data.slice(0, maxEntries)

  let outData = []

  for (let i = 0; i < sliceData.length; i += groupSize) {
    outData.push(sliceData.slice(i, i + groupSize))
  }

  return outData
}

export const useFooter = () => {
  const [footerData, setFooterData] = useState(data)
  const { topCities, getTopCities } = useTopCities()

  const updateFooterData = (entryName, newSubs) => {
    setFooterData((prevFooterData) =>
      prevFooterData.map((parent) =>
        parent.map((item) =>
          item.name === entryName
            ? {
              ...item,
              subs: newSubs.map((subGroup) =>
                subGroup.sort((a, b) => a.name.localeCompare(b.name)),
              ),
            }
            : item,
        ),
      ),
    )
  }

  const getTopBarristers = useCallback(() => {
    _data
      .get(`${Project.liveApi}legal-services/barristers/top-viewed/`)
      .then((barristers) => {
        const newSubs = splitData(barristers).map((columns) =>
          columns.map((barrister) => ({
            name: barrister.name,
            url: `/profile/barrister/${convertToUrl(barrister?.name)}-${barrister?.friendly_id
              }`,
          })),
        )

        updateFooterData('Barristers', newSubs)
      })
      .catch((error) => console.error(error))
  }, [])

  useEffect(() => {
    getTopBarristers()
  }, [getTopBarristers])

  const getTopLawyers = useCallback(() => {
    _data
      .get(`${Project.liveApi}legal-services/top-lawyers/`)
      .then((lawyers) => {
        const newSubs = splitData(lawyers).map((columns) =>
          columns.map((lawyer) => ({
            name: lawyer.name || lawyer.title || 'NA',
            url: `/profile/lawyer/${convertToUrl(lawyer?.name)}-${lawyer?.friendly_id
              }`,
          })),
        )

        updateFooterData('Top Lawyers', newSubs)
      })
      .catch((error) => console.error(error))
  }, [])

  useEffect(() => {
    getTopLawyers()
  }, [getTopLawyers])

  const getLawAreas = useCallback(() => {
    _data
      .get(`${Project.liveApi}law-categorisation/get-discover-law-data`)
      .then((lawAreas) => {
        const newSubs = splitData(lawAreas).map((columns) =>
          columns.map((lawArea) => ({
            name: lawArea.area.master_term,
            url: `/legal-field/${convertToUrl(lawArea.area.master_term)}`,
          })),
        )

        updateFooterData('Law Areas', newSubs)
      })
      .catch((error) => console.error(error))
  }, [])

  useEffect(() => {
    getLawAreas()
  }, [getLawAreas])

  useEffect(() => {
    getTopCities()
  }, [getTopCities])

  useEffect(() => {
    if (topCities.length > 0) {
      const newSubs = splitData(topCities).map((columns) =>
        columns.map((city: City) => ({
          name: city.label,
          url: `/find-a-lawyer/search?city=${city.label}`,
        })),
      )
      updateFooterData('Top UK Cities', newSubs)
    }
  }, [topCities])

  const getTopSolicitors = useCallback(() => {
    _data
      .get(
        `${Project.liveApi}legal-services/lawyers/top-solicitors/?limit=30&views_order=desc&lawyer_type=solicitor`,
      )
      .then((topSolicitors) => {
        const newSubs = splitData(topSolicitors).map((columns) =>
          columns.map((solicitor) => ({
            name: solicitor.name,
            url: `/profile/solicitor/${convertToUrl(solicitor?.name)}-${solicitor?.friendly_id
              }`,
          })),
        )

        updateFooterData('Solicitors', newSubs)
      })
      .catch((error) => console.error(error))
  }, [])

  useEffect(() => {
    getTopSolicitors()
  }, [getTopSolicitors])

  const getTopLawFirms = useCallback(() => {
    _data
      .get(`${Project.liveApi}legal-services/organisation/top-law-firms`)
      .then((lawFirms) => {
        const newSubs = splitData(lawFirms).map((columns) =>
          columns.map((lawFirm) => ({
            name: `${lawFirm.practice_name}`,
            url: `/profile/lawfirm/${convertToUrl(lawFirm.practice_name)}-${lawFirm?.friendly_id
              }`,
          })),
        )

        updateFooterData('Law Firms', newSubs)
      })
      .catch((error) => console.error(error))
  }, [])

  useEffect(() => {
    getTopLawFirms()
  }, [getTopLawFirms])

  const getTopChambers = useCallback(() => {
    _data
      .get(`${Project.liveApi}legal-services/chambers/top-chambers/`)
      .then((chambers) => {
        const newSubs = splitData(chambers).map((columns) =>
          columns.map((chamber) => ({
            name: chamber.name || 'NA',
            url: `/profile/chamber/${convertToUrl(chamber?.name)}-${chamber?.friendly_id
              }`,
          })),
        )

        updateFooterData('Chambers', newSubs)
      })
      .catch((error) => console.error(error))
  }, [])

  useEffect(() => {
    getTopChambers()
  }, [getTopChambers])

  return { footerData }
}
